import { useEffect, useMemo, useRef, useState } from 'react';

import { animated, config, useSpring } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';

import { getVisibleTickMarks } from '@da-g3-acl/shared/common';
import { getYLocationForDepthInM } from '@da-g3-acl/shared/common';
import { TickEnum } from '@da-g3-acl/shared/common';

import { guardSvgElementDimension } from '../helpers/guard-svg-element-dimension';
import {
	ImageToRulerLengthAllocations,
	useImageToRulerAllocationUtilities,
} from '../helpers/image-to-ruler-allocation';
import { FULL_RANGE_RULER_WIDTH_PX } from '../types/constants';
import { LengthRange } from '../types/lengthRange';

export interface FullRangeRulerProps {
	xStart: number | string;
	viewerHeightPx: number;
	lengthAllocations: ImageToRulerLengthAllocations | undefined;
	imageVisibleRange: LengthRange | undefined;
	handlePan: (delta: number) => void;
	setRange: (range: LengthRange) => void;
	offset: number;
}

const FullRangeRuler = (props: FullRangeRulerProps): JSX.Element => {
	const { viewerHeightPx, lengthAllocations, imageVisibleRange, xStart, setRange, offset } = props;

	const {
		rulerDepth: fullRange,
		getRulerDepthFromImageDepth,
		getImageDepthFromRulerDepth,
	} = useImageToRulerAllocationUtilities(lengthAllocations);

	const downholeVisibleRange = useMemo(
		() =>
			imageVisibleRange
				? {
						depthFrom:
							getRulerDepthFromImageDepth(imageVisibleRange.depthFrom) ?? imageVisibleRange.depthFrom,
						depthTo: getRulerDepthFromImageDepth(imageVisibleRange.depthTo) ?? imageVisibleRange.depthTo,
					}
				: undefined,
		[getRulerDepthFromImageDepth, imageVisibleRange],
	);

	const rulerElementRef = useRef<SVGSVGElement>(null);
	const coreImageryRange = fullRange && fullRange.depthTo - fullRange.depthFrom;
	const maxPxPerM = coreImageryRange && viewerHeightPx / coreImageryRange;

	const visibleTickMarks = getVisibleTickMarks({
		pxPerM: maxPxPerM ?? 0,
		rulerRangeM: fullRange,
		labelFrequencyTicks: 5,
	});

	const depthHandleElementRef = useRef<SVGRectElement>(null);
	const [wasDragged, setWasDragged] = useState<boolean>();

	const startPx =
		downholeVisibleRange &&
		fullRange &&
		maxPxPerM &&
		(downholeVisibleRange.depthFrom - fullRange.depthFrom) * maxPxPerM;
	const endPx =
		downholeVisibleRange &&
		fullRange &&
		maxPxPerM &&
		(downholeVisibleRange.depthTo - fullRange.depthFrom) * maxPxPerM;

	const [rulerAnimationComplete, setRulerAnimationComplete] = useState(true);

	const [{ startIndicatorPxAnimated, endIndicatorPxAnimated }, animatedIndicatorsRef] = useSpring(() => ({
		to: {
			startIndicatorPxAnimated: guardSvgElementDimension(startPx ?? 0),
			endIndicatorPxAnimated: guardSvgElementDimension(endPx ?? 0),
		},
		delay: 0,
		onStart: (): void => setRulerAnimationComplete(false),
		onRest: (): void => setRulerAnimationComplete(true),
		config: {
			...config.stiff,
			clamp: false,
		},
	}));

	useEffect(() => {
		animatedIndicatorsRef.start({
			startIndicatorPxAnimated: startPx,
			endIndicatorPxAnimated: endPx,
		});
	}, [startPx, endPx, animatedIndicatorsRef]);

	useGesture(
		{
			onDrag: ({ event, tap }) => {
				setWasDragged(true);
				if (!fullRange) return;
				if (!maxPxPerM) return;
				if (!downholeVisibleRange) return;
				if (!tap) {
					const cursorPositionRelative = (event as MouseEvent).offsetY / viewerHeightPx;
					const mAtCursor = cursorPositionRelative * coreImageryRange + fullRange.depthFrom;
					const newRange = calculateNewRange(mAtCursor, offset);
					newRange && setRange(newRange);
				}
			},
		},
		{
			target: depthHandleElementRef,
			eventOptions: {},
		},
	);

	useGesture(
		{
			onClick: ({ event }) => {
				if (!fullRange) return;
				if (!coreImageryRange) return;
				if (!wasDragged) {
					const cursorPositionRelative = (event as MouseEvent).offsetY / viewerHeightPx;
					const mAtCursor = cursorPositionRelative * coreImageryRange + fullRange.depthFrom;
					const newRange = calculateNewRange(mAtCursor, offset);
					newRange && setRange(newRange);
				}

				setWasDragged(undefined);
			},
		},
		{
			target: rulerElementRef,
		},
	);

	const calculateNewRange = (newCentrePoint: number, currentOffset: number): LengthRange | undefined => {
		if (!downholeVisibleRange) return;
		if (!fullRange) return;
		const visibleRangeM = downholeVisibleRange.depthTo - downholeVisibleRange.depthFrom;
		let offset = 0;

		const updatedFrom = newCentrePoint - 0.5 * visibleRangeM + currentOffset;
		const updatedTo = newCentrePoint + 0.5 * visibleRangeM + currentOffset;

		//Recalculated range starts before core imagery range -> offset so new range starts at beginning
		if (updatedFrom < fullRange.depthFrom) offset = fullRange.depthFrom - updatedFrom;

		//Recalculated range ends after core imagery range -> offset so new range ends at end of range.
		if (updatedTo > fullRange.depthTo) offset = fullRange.depthTo - updatedTo;

		return {
			depthFrom: getImageDepthFromRulerDepth(updatedFrom + offset) ?? fullRange.depthFrom,
			depthTo: getImageDepthFromRulerDepth(updatedTo + offset) ?? fullRange.depthTo,
		};
	};

	return (
		<svg
			x={xStart}
			height={'100%'}
			ref={rulerElementRef}
			width={FULL_RANGE_RULER_WIDTH_PX}
			style={{ background: 'white' }}
		>
			<line x1={0} x2={0} y1={0} y2={'100%'} stroke={'#283C50'} strokeWidth={2} />
			<foreignObject x="0" y="0" height="100%" width="100%">
				<svg height={viewerHeightPx} width={FULL_RANGE_RULER_WIDTH_PX} style={{ cursor: 'pointer' }}>
					<g>
						{visibleTickMarks.map((r) => (
							<g key={`tick-${r.tickDepthMetres}`}>
								<line
									x1={`${r.type === TickEnum.Long || r.type === TickEnum.Label ? 80 : 90}%`}
									x2={'100%'}
									y1={getYLocationForDepthInM(
										maxPxPerM ?? 1,
										r.tickDepthMetres,
										fullRange?.depthFrom ?? 0,
									)}
									y2={getYLocationForDepthInM(
										maxPxPerM ?? 1,
										r.tickDepthMetres,
										fullRange?.depthFrom ?? 0,
									)}
									stroke={'#283C50'}
									strokeWidth={1}
									shapeRendering={'crispEdges'}
								></line>
								{r.type === TickEnum.Label && (
									<text
										style={{
											stroke: '#283C50',
											fontSize: 8,
											fontWeight: 'lighter',
											transform: `translateX(${0 - 8 - 3}px)`,
											pointerEvents: 'none',
										}}
										x={`100%`}
										y={
											getYLocationForDepthInM(
												maxPxPerM ?? 1,
												r.tickDepthMetres,
												fullRange?.depthFrom ?? 0,
											) + 2.5
										}
										textAnchor={'end'}
										shapeRendering={'crispEdges'}
										textRendering={'optimizeLegibility'}
									>
										{r.tickDepthMetres}
									</text>
								)}
							</g>
						))}
					</g>
				</svg>
			</foreignObject>

			<animated.rect
				className={'rulerDepthHandle'}
				ref={depthHandleElementRef}
				style={{
					cursor: wasDragged ? 'grabbing' : 'grab',
				}}
				fill={'#A9B1B9'}
				fillOpacity={0.4}
				stroke={'#435BB7'}
				strokeWidth={1}
				x={0}
				width={25}
				y={startIndicatorPxAnimated}
				height={endIndicatorPxAnimated.to((e) => Math.max(2, e - startIndicatorPxAnimated.get() - 1))}
				data-ruler-animation-complete={rulerAnimationComplete}
			></animated.rect>
		</svg>
	);
};

export default FullRangeRuler;
